import { ClosableDialog } from '@counsel-project/components'
import { CustomMeasure } from '@counsel-project/counsel-audit-backend-api'
import { DOCUMENT_TYPES } from '@counsel-project/counsel-generation-api'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

export type AddMeasurementDialogProps = {
  open: boolean
  onClose: () => void
  onAdd: (measurement: CustomMeasure) => void
}

export default function AddMeasurementDialog({
  open,
  onClose,
  onAdd,
}: Readonly<AddMeasurementDialogProps>) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')

  const handleAdd = () => {
    const newMeasurement: CustomMeasure = {
      id: uuidv4(),
      title: title.trim(),
      description: description.trim(),
      severity: 0,
      // New measures default to every detected document type ("All Detected")
      documentTypes: [...DOCUMENT_TYPES],
    }
    onAdd(newMeasurement)
    handleClose()
  }

  const handleClose = () => {
    setTitle('')
    setDescription('')
    onClose()
  }

  const isValid = title.trim() && description.trim()

  return (
    <ClosableDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      title="Add New Measurement Criteria"
    >
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Create custom measurement criteria for the AI to check in clinical documentation. For
          example: "Ensure medical necessity is documented in care plans" or "Check for risk
          assessment in intake forms".
        </Typography>
        <Grid container spacing={3} direction="column">
          <Grid item>
            <Typography variant="body1" fontWeight={500}>
              Measurement Title
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Include the title for the type of alert this measure will trigger
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Eg. 'Ensure medical necessity is documented in care plans'"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              inputProps={{ 'data-qa-id': 'measurement-dialog-title-input' }}
            />
          </Grid>
          <Grid item>
            <Typography variant="body1" fontWeight={500}>
              AI Instructions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Describe to the AI what to check for in this measure
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Description of what to check for in this measure"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={4}
              inputProps={{ 'data-qa-id': 'measurement-dialog-instructions-input' }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          color="inherit"
          data-qa-id="measurement-dialog-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          color="primary"
          disabled={!isValid}
          data-qa-id="measurement-dialog-submit-button"
        >
          Add Measurement
        </Button>
      </DialogActions>
    </ClosableDialog>
  )
}
